<template>
    <TprmModal
        open
        size="xl"
        :additional-class-name="additionalClasses"
        :title="`Submission History for ${entity.displayName}`"
        @close="close"
    >
        <LogoLoader v-if="loading" class="h-full w-full flex items-center justify-center" />
        <DocumentSubmissionHistoryTable
            v-else
            :submissions="historicDocumentSubmissions"
            @show-document="showDocument"
        />
    </TprmModal>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import difference from 'lodash/difference';
    import orderBy from 'lodash/orderBy';

    import { LogoLoader } from '@evidentid/dashboard-commons/components/LogoLoader';
    import { TprmRequirementCategory, TprmRequirementDefinition } from '@evidentid/tprm-portal-lib/models/common';
    import {
        TprmRequirementInstanceModel,
    } from '@evidentid/tprm-portal-lib/models/common/TprmRequirementInstanceModel.model';
    import { Entity } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { DeclineReason, TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { EntityRequirementDetails } from '@evidentid/tprm-portal-lib/models/entity-details/EntityRequirement.model';
    import { TprmRequirementModel } from '@evidentid/tprm-portal-lib/models/entity-details/TprmRequirementModel.model';
    import {
        DeclineReasonPerInstance,
        VerificationRequest,
    } from '@evidentid/tprm-portal-lib/models/notification-configuration/VerificationRequest.model';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import TprmModal from '@/components/tprm-modal/TprmModal.vue';
    import { HistoricDocumentSubmission } from '@/models/HistoricDocumentSubmission.model';
    import DocumentSubmissionHistoryTable
        from '@/modules/entity-details/components/DocumentSubmissionHistoryTable/DocumentSubmissionHistoryTable.vue';
    import { useEntityDetailsStore } from '@/modules/entity-details/store';
    import {
        flattenSingleOrMultiRequirementType,
    } from '@/modules/entity-details/utils/flattenSingleOrMultiRequirementType/flattenSingleOrMultiRequirementType';
    import { getRequirementInstanceLabel } from '@/utils/get-requirement-instance-label/getRequirementInstanceLabel';
    import { useDashboardStore } from '@/modules/dashboard/vuex/dashboard';

    type VerificationId = string;

    // PRODUCT-26110 QA fixes4: declares the `abort` event this procedure component
    // `$emit`s -- without a declared `emits`, Vue 3 warns "Component emitted event
    // 'abort' but it is neither declared in the emits option nor as an 'onAbort' prop"
    // every time it fires.
    export default defineComponent({
        name: 'ViewHistoricDocumentProcedure',
        components: {
            DocumentSubmissionHistoryTable,
            LogoLoader,
            TprmModal,
        },
        // PRODUCT-26110 QA fixes4: `uuid` is unused; declared purely so Vue 3 doesn't warn
        // "Extraneous non-props attributes (uuid)" (see UploadEntityDocumentProcedure.vue).
        props: {
            uuid: {
                type: String,
                default: '',
            },
            entity: {
                type: Object as PropType<Entity>,
                required: true,
            },
            requirementModels: {
                type: Array as PropType<TprmRequirementModel[]>,
                required: true,
            },
        },
        emits: [ 'abort' ],
        data() {
            return {
                open: true,
                showingDocument: false,
                onEntityDetailsView: false,
            };
        },
        computed: {
            rpName(): string {
                // TODO(PRODUCT-18675): remove any after the migration to the newer version. apply to whole file
                return (this as any).$rp.current!;
            },
            loading(): boolean {
                return this.loadingVerifications ||
                    this.loadingRequirementDetails;
            },
            loadingVerifications(): boolean {
                return this.verificationsStatus.status === OperationStatus.loading;
            },
            loadingRequirementDetails(): boolean {
                return this.requirementDetailsStatus.status === OperationStatus.loading;
            },
            requirementDefinitions(): Record<TprmRequirementType, TprmRequirementDefinition> {
                return useDashboardStore().tprmRequirementsDefinitions || {};
            },
            requirementInstanceModels(): Record<TprmRequirementCategory, TprmRequirementInstanceModel[]> {
                return useDashboardStore().requirementInstanceModels || {};
            },
            verificationsStatus(): { status: OperationStatus, list: VerificationRequest[] } {
                return useEntityDetailsStore().verificationRequestsStatus[this.rpName] || {
                    status: OperationStatus.uninitialized,
                    list: [],
                };
            },
            requirementDetailsStatus(): { status: OperationStatus, list: EntityRequirementDetails[] } {
                return useEntityDetailsStore().requirementDetailsListStatus[this.rpName] || {
                    status: OperationStatus.uninitialized,
                    list: [],
                };
            },
            historicDocumentSubmissions(): HistoricDocumentSubmission[] {
                const submissions = [] as HistoricDocumentSubmission[];
                this.verificationsStatus.list.forEach((verification) => {
                    const isEligibleVerification = !verification.cancelledAt &&
                        [ 'COMPLETE', 'NO_INSURED_RESPONSE', 'PROCESSING', 'ACTIONS_REVIEW', 'AUDIT', 'MEMBER_OUTREACH', 'PRODUCER_OUTREACH' ].includes(verification.verificationRequestStatus);

                    if (isEligibleVerification) {
                        const notProvidedRequirementTypes = this.getNotProvidedRequirementTypes(verification);
                        const currentMap: Record<VerificationId, TprmRequirementType[]> =
                            this.requirementDetailsStatus.list.reduce((accu, details) => {
                                // coverage details' request id IS verification id
                                const verificationId = details.coverage?.requestId;
                                const types = verificationId && accu[verificationId] || [];
                                return {
                                    ...accu,
                                    ...(verificationId && { [verificationId]: [ ...types, details.coverageType ] }),
                                };
                            }, {} as Record<VerificationId, TprmRequirementType[]>);
                        verification.reasons.forEach(
                            (reason) => {
                                const isCurrentSubmission =
                                    currentMap[verification.verificationId]?.includes(reason.coverageType) || false;
                                submissions.push(this.constructSubmission({
                                    verification, requirementType: reason.coverageType, notProvidedRequirementTypes,
                                    isCurrentSubmission, requirementInstanceId: reason.instanceId,
                                }));
                            },
                        );
                    }
                });
                return orderBy(
                    submissions,
                    [ 'requestDate', 'coverageTypeLabel', 'requirementInstanceLabel' ],
                    [ 'desc', 'asc', 'asc' ],
                );
            },
            additionalClasses(): string {
                const hide = this.showingDocument ? 'invisible' : '';
                return `ViewHistoricDocumentModal ${hide}`;
            },
        },
        mounted(): void {
            this.onEntityDetailsView = Boolean(this.$route.name === 'dashboard' && this.$route.query.entity);
            useEntityDetailsStore().loadVerificationRequests({
                rpName: this.rpName,
                entityId: this.entity.id,
            });
            if (!this.onEntityDetailsView) {
                useEntityDetailsStore().loadRequirementDetailsList({
                    rpName: this.rpName,
                    entityId: this.entity.id,
                });
            }
        },
        unmounted(): void {
            useEntityDetailsStore().clearVerificationRequests({ rpName: this.rpName });
            if (!this.onEntityDetailsView) {
                useEntityDetailsStore().clearRequirementDetailsList({ rpName: this.rpName });
            }
        },
        methods: {
            close(): void {
                this.$emit('abort');
                this.open = false;
            },
            showDocument({ submission, customNoDocMsg }: {
                submission: HistoricDocumentSubmission;
                customNoDocMsg?: string;
            }): void {
                this.showingDocument = true;
                // TODO(PRODUCT-18675): remove "as any" after the migration to the newer version. apply to whole file
                (this as any).$procedures.execute('showEntityRequirementRprDocuments', {
                    rpName: this.rpName,
                    email: this.entity?.contactEmail || '',
                    requirementType: submission.coverageType,
                    requirementInstanceId: submission.requirementInstanceId,
                    requirementInstanceLabel: submission.requirementInstanceLabel,
                    rprId: submission.rprId,
                    verificationId: submission.verificationId,
                    customNoDocMsg,
                }, this.onDocumentClosed);
            },
            onDocumentClosed(): void {
                this.showingDocument = false;
            },
            getNotProvidedRequirementTypes(verification: VerificationRequest): TprmRequirementType[] {
                const requestedRequirementTypes = difference(
                    verification.reasons.map((x) => x.coverageType),
                    verification.cancelledCoverageTypes?.map(flattenSingleOrMultiRequirementType) || [],
                );
                return difference(
                    requestedRequirementTypes,
                    verification.extractedCoverageTypes?.map(flattenSingleOrMultiRequirementType) || [],
                );
            },
            constructSubmission(
                {
                    verification, requirementType, notProvidedRequirementTypes, isCurrentSubmission,
                    requirementInstanceId,
                }: {
                    verification: VerificationRequest;
                    requirementType: TprmRequirementType;
                    notProvidedRequirementTypes: TprmRequirementType[];
                    isCurrentSubmission: boolean;
                    requirementInstanceId?: string | null;
                },
            ): HistoricDocumentSubmission {
                const requirementInstanceLabel = requirementInstanceId
                    ? getRequirementInstanceLabel(
                        requirementType,
                        this.requirementDefinitions,
                        requirementInstanceId,
                        this.requirementInstanceModels,
                    )
                    : null;

                // TODO[PRODUCT-24130]: refactor to use TprmRequirementDefinition instead of TprmRequirementModel
                // this model is passed externally, we need to prefer due to dashboard.vue pass in country specific
                // labels, we should eventually add such into to the model definitions on the BE and handle all here
                const preferredLabel = this.requirementModels.find(
                    (x) => x.coverageType === requirementType,
                )?.label;
                const labelByDefinition = this.requirementDefinitions[requirementType]?.defaultLabel;
                const requirementTypeLabel = preferredLabel || labelByDefinition || requirementType;
                const _declineReason = verification.declinedCoverageTypes?.[requirementType];
                const declineReason = requirementInstanceId
                    ? (_declineReason as DeclineReasonPerInstance)?.instances?.[requirementInstanceId]
                    : _declineReason as DeclineReason;
                const isExtractedRequirementType = (verification.extractedCoverageTypes || [])
                    .map(flattenSingleOrMultiRequirementType)
                    .includes(requirementType);

                return {
                    requestDate: verification.createdAt,
                    submissionDate: verification.inputReceivedAt,
                    coverageType: requirementType,
                    coverageTypeLabel: requirementTypeLabel,
                    rprId: verification.requestId,
                    verificationId: verification.verificationId,
                    insuredId: this.entity.id,
                    isNoResponse: Boolean(verification.timeoutAt) || !verification.inputReceivedAt,
                    isNotProvided: notProvidedRequirementTypes.includes(requirementType),
                    isExtractedRequirementType,
                    isCurrentSubmission,
                    declineReason,
                    submissionMethod: verification.submissionMethod,
                    requirementInstanceId,
                    requirementInstanceLabel,
                    verificationRequestStatus: verification.verificationRequestStatus,
                };
            },
        },
    });
</script>
